import { Dialog, Popover, PopoverContent, PopoverTrigger, SegmentedControl } from '@/_components/ui'
import { formatCurrency } from '@/_helpers/currency'
import { formatPercentage } from '@/_helpers/percentage'
import { cn } from '@/_lib/utils'
import type {
  TBulkSimulateApplicationInputDTO,
  TSimulateApplicationOutputDTO,
} from '@doutorsim/shared/applications'
import type { TExposedFacilityDTO } from '@doutorsim/shared/facilities'
import dayjs from 'dayjs'
import { ChevronDown } from 'lucide-react'
import { useMemo } from 'react'
import { useFacilityStore } from '../../_stores'
import { CreatePersonButton } from '../../applications/_components/create-person-button'
import { InstallmentDropItem } from './installment-drop-item'
import { InstallmentsDetailsDialog } from './installments-details-dialog'

const HIGHLIGHTED_MONTHS = [6, 12, 18, 24]

interface SimulationResultsPanelProps {
  simulationData: TBulkSimulateApplicationInputDTO | null
  installments: TSimulateApplicationOutputDTO[]
  selectedInstallment: TSimulateApplicationOutputDTO | null
  isLoading: boolean
  onInstallmentSelection: (installment: TSimulateApplicationOutputDTO) => void
}

export const SimulationResultsPanel = ({
  simulationData,
  installments,
  selectedInstallment,
  isLoading,
  onInstallmentSelection,
}: SimulationResultsPanelProps) => {
  const facility = useFacilityStore.getState().currentFacility

  const highlightedInstallments = useMemo(
    () => installments.filter((i) => HIGHLIGHTED_MONTHS.includes(i.numPayments)),
    [installments],
  )

  const currentInstallment =
    selectedInstallment ?? highlightedInstallments[1] ?? highlightedInstallments[0]

  const hasData = !isLoading && simulationData && installments.length > 0

  const panelBase = 'bg-brand-green-primary-1 p-8 lg:p-10 flex flex-col gap-6 min-h-full'

  const skeletonClass = isLoading ? 'animate-pulse' : ''

  return (
    <div className={panelBase}>
      {/* Header */}
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-brand-green-primary-5">
          Resultado da simulação
        </p>
        <h2 className="mt-2 text-lg font-semibold text-brand-grayscale-12">
          Veja as condições da sua simulação abaixo.
        </h2>
      </div>

      {/* Hero Number */}
      <div className="space-y-1">
        <p className="text-sm font-semibold text-brand-green-primary-8">Parcela mensal</p>
        {hasData && currentInstallment ? (
          <p className="text-4xl font-extrabold leading-tight text-brand-green-primary-5">
            {`${currentInstallment.numPayments}x de ${formatCurrency(currentInstallment.monthlyPaymentAmount)}`}
          </p>
        ) : (
          <div className={cn('h-9 w-64 rounded-lg bg-brand-green-primary-2', skeletonClass)} />
        )}
        {hasData && currentInstallment ? (
          <p className="text-base text-brand-grayscale-7">
            {`Você recebe ${formatCurrency(currentInstallment.requestedAmount)}`}
          </p>
        ) : (
          <div className={cn('h-5 w-48 rounded bg-brand-green-primary-2', skeletonClass)} />
        )}
      </div>

      {/* Installment selector */}
      <div>
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-brand-grayscale-7">
          Parcelas
        </p>
        <div className="flex items-center gap-3">
          <SegmentedControl
            disabled={!hasData}
            value={currentInstallment?.numPayments}
            options={
              hasData
                ? highlightedInstallments.map((i) => ({
                    value: i.numPayments,
                    label: `${i.numPayments}x`,
                  }))
                : HIGHLIGHTED_MONTHS.map((n) => ({ value: n, label: `${n}x`, disabled: true }))
            }
            onChange={(numPayments) => {
              const installment = highlightedInstallments.find((i) => i.numPayments === numPayments)
              if (installment) onInstallmentSelection(installment)
            }}
          />
          {hasData && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1 text-sm font-bold text-brand-green-primary-5 hover:underline"
                >
                  Mais
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </PopoverTrigger>
              <PopoverContent
                side="bottom"
                align="start"
                className="scrollbar max-h-[300px] min-w-[307px] overflow-y-auto"
              >
                <div className="flex flex-col gap-1 pr-2">
                  {installments.map((installment) => (
                    <Dialog key={installment.numPayments}>
                      <InstallmentDropItem
                        installment={installment}
                        selected={currentInstallment?.numPayments === installment.numPayments}
                        onClick={() => onInstallmentSelection(installment)}
                      />
                      <InstallmentsDetailsDialog
                        simulationData={simulationData}
                        installment={installment}
                        selectInstallment={() => onInstallmentSelection(installment)}
                      />
                    </Dialog>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>

      {/* Details */}
      <div className="space-y-3 border-t border-brand-green-primary-2 pt-5 text-sm">
        <DetailRow
          label="Taxa mensal"
          value={
            hasData && currentInstallment
              ? `${formatPercentage(currentInstallment.monthlyInterestRate)} a.m.`
              : undefined
          }
          loading={isLoading}
        />
        <DetailRow
          label="Primeira parcela"
          value={
            hasData && simulationData
              ? dayjs(simulationData.firstPaymentDate).format('DD/MM/YYYY')
              : undefined
          }
          loading={isLoading}
        />
      </div>

      {/* CTA */}
      <CreatePersonButton
        company={facility as TExposedFacilityDTO}
        simulationData={simulationData ?? undefined}
        selectedInstallment={selectedInstallment ?? undefined}
        label="Seguir com proposta"
        className="h-10 w-full"
        type="button"
        disabled={!selectedInstallment}
      />
    </div>
  )
}

const DetailRow = ({
  label,
  value,
  loading,
}: {
  label: string
  value?: string
  loading?: boolean
}) => (
  <div className="flex items-center justify-between">
    <span className="text-brand-grayscale-7">{label}</span>
    {value ? (
      <span className="font-semibold text-brand-grayscale-12">{value}</span>
    ) : (
      <div
        className={cn('h-4 w-20 rounded bg-brand-green-primary-2', loading && 'animate-pulse')}
      />
    )}
  </div>
)
